import { z } from 'zod';
import { moneySchema } from './pricing';
// ───────────────────────────── enums and labels ─────────────────────────────
export const paymentStatusSchema = z.enum(['pending', 'paid', 'expired', 'failed']);
export const paymentChannelTypeSchema = z.enum(['transfer', 'qris', 'va_closed']);
export const settledViaSchema = z.enum(['aiyo_webhook', 'manual_admin']);
export const feeTriggerSchema = z.enum(['on_order', 'on_payment']);
export const ledgerAccountKindSchema = z.enum(['asset', 'liability']);
export const ledgerAccountOwnerSchema = z.enum([
    'platform', 'pmi_branch', 'pmi_national', 'aiyo', 'partner',
]);
export const ledgerTransactionKindSchema = z.enum([
    'payment_settlement', 'withdrawal', 'adjustment',
]);
export const PAYMENT_STATUS_LABELS = {
    pending: 'Menunggu pembayaran',
    paid: 'Lunas',
    expired: 'Kedaluwarsa',
    failed: 'Gagal dibuat',
};
export const PAYMENT_CHANNEL_LABELS = {
    transfer: 'Transfer Bank',
    qris: 'QRIS',
    va_closed: 'Virtual Account',
};
export const FEE_TRIGGER_LABELS = {
    on_order: 'Per pemesanan',
    on_payment: 'Per pembayaran',
};
export const SETTLED_VIA_LABELS = {
    aiyo_webhook: 'Otomatis (AiYO)',
    manual_admin: 'Manual oleh admin',
};
export const LEDGER_TRANSACTION_KIND_LABELS = {
    payment_settlement: 'Pelunasan pembayaran',
    withdrawal: 'Pencairan saldo',
    adjustment: 'Koreksi',
};
/** Statuses in which a payment still holds its orders. */
export const PAYMENT_OPEN_STATUSES = ['pending', 'paid'];
/**
 * The channel codes AiYO uses. Deliberately NOT hard-coded as a closed list on the wire —
 * an admin configures the catalogue — but these are the ones the integration document
 * names, so they seed the picker.
 */
export const KNOWN_BANK_CODES = [
    { code: '503', label: 'QRIS', type: 'qris' },
    { code: '008', label: 'VA Mandiri', type: 'va_closed' },
    { code: '009', label: 'VA BNI', type: 'va_closed' },
    { code: '002', label: 'VA BRI', type: 'va_closed' },
    { code: '014', label: 'VA BCA', type: 'va_closed' },
];
/** Maps our snake_case channel onto the UPPERCASE `type` AiYO expects. */
export function toAiyoChannelType(t) {
    return t === 'transfer' ? 'TRANSFER' : t === 'qris' ? 'QRIS' : 'VA_CLOSED';
}
/** The inverse. Unknown values are rejected rather than guessed. */
export function fromAiyoChannelType(t) {
    switch (t?.toUpperCase()) {
        case 'TRANSFER': return 'transfer';
        case 'QRIS': return 'qris';
        case 'VA_CLOSED': return 'va_closed';
        default: return null;
    }
}
// ───────────────────────────── input ─────────────────────────────
export const feeRecipientInputSchema = z.object({
    ledgerAccountId: z.string().uuid(),
    /** Rupiah as a string, like every other amount — see the note on `moneySchema`. */
    amount: moneySchema.refine((v) => BigInt(v) > 0n, 'Bagian penerima harus lebih dari nol'),
});
/**
 * A fee rule and its whole split arrive together, because a rule whose recipients do not
 * sum to its amount is rejected by the database. Splitting them across two requests would
 * only produce a rule that can never be saved.
 */
export const feeRuleInputSchema = z.object({
    trigger: feeTriggerSchema,
    amount: moneySchema,
    effectiveFrom: z.coerce.date().optional(),
    note: z.string().max(500).optional(),
    recipients: z.array(feeRecipientInputSchema).max(20).default([]),
}).superRefine((v, ctx) => {
    const total = v.recipients.reduce((sum, r) => sum + BigInt(r.amount), 0n);
    if (total !== BigInt(v.amount)) {
        ctx.addIssue({
            code: 'custom',
            path: ['recipients'],
            message: `Pembagian penerima berjumlah ${total}, seharusnya ${BigInt(v.amount)}. `
                + 'Setiap rupiah biaya harus punya penerima.',
        });
    }
    const seen = new Set();
    for (const r of v.recipients) {
        if (seen.has(r.ledgerAccountId)) {
            ctx.addIssue({
                code: 'custom', path: ['recipients'],
                message: 'Satu akun tidak boleh muncul dua kali dalam satu aturan biaya.',
            });
        }
        seen.add(r.ledgerAccountId);
    }
});
/**
 * Which orders to pay for. The checkbox selection from the hospital's finance screen.
 *
 * No channel is chosen here: the invoice is created WITHOUT a payment method and the
 * method is attached afterwards, exactly as the integration document prescribes. That is
 * what lets a hospital change its mind about the channel without a new invoice.
 */
export const paymentInputSchema = z.object({
    orderIds: z.array(z.string().uuid()).min(1, 'Pilih minimal satu pesanan').max(100),
});
export const selectMethodInputSchema = z.object({
    type: paymentChannelTypeSchema,
    bankCode: z.string().min(1).max(10),
});
export const manualSettleInputSchema = z.object({
    /** Required: a manual settlement with no stated reason is unauditable. */
    note: z.string().min(3, 'Sebutkan dasar pelunasan manual ini').max(500),
    paidAt: z.coerce.date().optional(),
});
export const withdrawalInputSchema = z.object({
    accountId: z.string().uuid(),
    amount: moneySchema.refine((v) => BigInt(v) > 0n, 'Nominal pencairan harus lebih dari nol'),
    occurredAt: z.coerce.date().optional(),
    reference: z.string().max(200).optional(),
    note: z.string().max(500).optional(),
});
/**
 * A reversing correction. The ONLY way to change the ledger, because entries are
 * append-only — an edited ledger cannot be reconciled against a bank statement.
 */
export const ledgerAdjustmentInputSchema = z.object({
    reversesTransactionId: z.string().uuid(),
    reason: z.string().min(5, 'Sebutkan alasan koreksi').max(500),
});
